import { useState, useEffect } from "react";
import { Box, Button, useTheme, Switch, CircularProgress, FormControl, InputLabel, MenuItem, FormControlLabel } from "@mui/material";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

import { fetchAllProjectsRecursive, fetchAllSites, fetchAllUnitsForSiteRecursive, fetchExportAlerts } from "../../services/apis";
import { formatDate } from "../../utils/timestamp";
import { sortByKey } from "../../utils/sortByKey";

export interface SelectItem {
  id: string;
  name: string;
}

export interface UnitsRequestData {
  search_substring?: string;
  pageNumber?: string;
  pageSize?: string;
  sortOrder?: string;
  sortField?: string;
  site_Id: string;
}
export default function ExportAlerts({ exportAlertsData, isLoadingAlerts, isSuccessAlerts, setAlertsRequestData, alertsFilteredRows }: any) {
  const theme = useTheme();
  const queryClient = useQueryClient();
  const [project, setProject] = useState<SelectItem>({ id: "all", name: "All" });
  const [site, setSite] = useState<SelectItem>({ id: "all", name: "All" });
  const [unit, setUnit] = useState<SelectItem>({ id: "all", name: "All" });
  const [isActiveAlertsChecked, setIsActiveAlertsChecked] = useState(true);

  const { data: projects, isLoading: isProjectsLoading, isError: isProjectsError, error: projectsError } = useQuery({ queryKey: ["projects"], queryFn: fetchAllProjectsRecursive });

  const {
    data: sites,
    isLoading: isSitesLoading,
    isError: isSitesError,
    error: sitesError,
  } = useQuery({ queryKey: ["sites", { projectId: project?.id }], queryFn: fetchAllSites, enabled: !!project && project?.id !== "all" });

  const {
    data: units,
    isLoading: isUnitsLoading,
    isError: isUnitsError,
    error: unitsError,
  } = useQuery({
    queryKey: ["units", { siteId: site?.id }],
    queryFn: ({ queryKey }) => fetchAllUnitsForSiteRecursive((queryKey[1] as { siteId: string }).siteId),
    enabled: !!site && site?.id !== "all",
  });

  const handleSubmitAllInfo = async () => {
    if (exportAlertsData && !isLoadingAlerts && isSuccessAlerts && alertsFilteredRows) {
      if (alertsFilteredRows?.length > 0) {
        // Map the alerts to match the desired headers
        const convertedAlerts = alertsFilteredRows?.map((alert: any) => ({
          "Time Started": formatDate(alert.first_seen) || "N/A", // Map the "formattedTimestamp" field to "Date"
          "Time Ended": formatDate(alert.last_seen) || "N/A", // Map the "formattedTimestamp" field to "Date"
          Description: alert.alertDescription || "N/A", // Map the "alert_id" field to "Alert Description"
          Project: alert.projectName || "N/A", // Map the "project_name" field to "Project"
          Site: alert.siteName || "N/A", // Map the "site_name" field to "Site"
          Unit: alert.unit_name || "N/A", // Map the "unit_name" field to "Unit"
        }));
        console.log("convertedAlerts", convertedAlerts);

        // Format the file name with the current date and time
        const now = new Date();
        const formattedDate = now
          .toLocaleDateString("en-US", {
            month: "2-digit",
            day: "2-digit",
            year: "2-digit",
          })
          .replace(/\//g, "-");
        const formattedTime = now
          .toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            hour12: false,
          })
          .replace(":", "-");

        const fileName = `Activealerts_${formattedTime}_${formattedDate}.xlsx`;

        // Create a worksheet from the converted data
        const worksheet = XLSX.utils.json_to_sheet(convertedAlerts);

        // Create a new workbook and append the worksheet
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Active Alerts");

        // Write the workbook to a Blob and trigger the download
        const blob = new Blob([XLSX.write(workbook, { bookType: "xlsx", type: "array" })], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        saveAs(blob, fileName);
        queryClient.removeQueries({ queryKey: ["exportAlertsData"] });
        // setEnableExport(false);
      } else {
        // queryClient.removeQueries({ queryKey: ["exportAlertsData"] });
        // setEnableExport(false);
      }
    }
  };

  const handleChangeProject = (event: SelectChangeEvent) => {
    setIsActiveAlertsChecked(true);
    const projectId = event.target.value as string;
    if (projectId === "all") {
      setProject({ id: "all", name: "All" });
    } else {
      const selectedProject = projects?.find((p: any) => p.project_id === projectId);
      setProject(selectedProject ? { id: selectedProject.project_id, name: selectedProject.name } : { id: "all", name: "All" });
    }
    setSite({ id: "all", name: "All" });
    setUnit({ id: "all", name: "All" });
  };

  const handleChangeSite = (event: SelectChangeEvent) => {
    setIsActiveAlertsChecked(true);
    const siteId = event.target.value as string;
    if (siteId === "all") {
      setSite({ id: "all", name: "All" });
    } else {
      const selectedSite = sites?.data?.find((s: any) => s.site_id === siteId);
      setSite(selectedSite ? { id: selectedSite.site_id, name: selectedSite.site_name } : { id: "all", name: "All" });
    }
    setUnit({ id: "all", name: "All" });
  };

  const handleChangeUnit = (event: SelectChangeEvent) => {
    setIsActiveAlertsChecked(true);
    const unitId = event.target.value; // MUI gives string, convert to number if needed
    if (unitId === "all") {
      setUnit({ id: "all", name: "All" });
      return;
    }
    const selected = units?.data?.find((u: any) => u.unit_id === Number(unitId));
    setUnit(selected ? { id: selected.unit_id, name: selected.unit_name } : { id: "all", name: "All" });
  };

  useEffect(() => {
    setAlertsRequestData((prev: any) => ({
      ...prev,
      active_only: isActiveAlertsChecked,
      project_id: project?.id !== "all" && project?.id ? project?.id : "",
      site_id: site?.id !== "all" && site?.id ? site?.id : "",
      unit_id: unit?.id !== "all" && unit?.id ? unit?.id : "",
    }));
  }, [project, site, unit]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // console.log("isActiveAlertsChecked", event.target.checked);
    setIsActiveAlertsChecked(event.target.checked);
    setAlertsRequestData((prev: any) => ({
      ...prev,
      active_only: event.target.checked,
    }));
  };

  // console.log("units", units, "isUnitsError", isUnitsError, "unitsError", unitsError);
  // console.log("exportAlertsData", exportAlertsData);
  // console.log("alertsFilteredRows", alertsFilteredRows);

  return (
    <Box>
      {isProjectsError && <p>Error fetching projects: {projectsError.message}</p>}
      {(sites?.message === "No data found" && <p>Error fetching sites: {sites?.message}</p>) || (isSitesError && <p>Error fetching sites: {sitesError.message}</p>)}
      {(units?.message === "No data found" && <p>Error fetching units: {units?.message}</p>) || (isUnitsError && <p>Error fetching units: {unitsError.message}</p>)}
      <Box sx={{ display: "flex", alignItems: "center", flexDirection: "row", justifyContent: "space-between" }}>
        <Button
          variant="text"
          color="primary"
          startIcon={<SaveAltIcon />}
          onClick={handleSubmitAllInfo}
          style={{ border: "none", color: theme.palette.primary.main }}
          disabled={exportAlertsData?.length === 0} // Disable export if no data
        >
          EXPORT
        </Button>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "row" }}>
          <FormControlLabel
            sx={{
              marginRight: "40px",
            }}
            control={<Switch checked={isActiveAlertsChecked} onChange={handleChange} />}
            label="Only show active alerts"
          />
          {isProjectsLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                marginTop: "40px",
                marginBottom: "10px",
                minWidth: 150,
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <FormControl variant="standard" sx={{ minWidth: 150 }}>
              <InputLabel id="Select a Project InputLabel">Select a Project</InputLabel>
              <Select labelId="Select a Project labelId" id="demo-simple-select" value={project?.id || ""} label="Select a Project" onChange={handleChangeProject}>
                <MenuItem key={"all"} value={"all"}>
                  All
                </MenuItem>
                {sortByKey(projects, "name")?.map((project: any) => (
                  <MenuItem key={project?.project_id} value={project?.project_id}>
                    {project?.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          {isSitesLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                marginTop: "40px",
                marginBottom: "10px",
                minWidth: 150,
              }}
            >
              <CircularProgress size={24} />
            </Box>
          ) : (
            <>
              <FormControl variant="standard" sx={{ minWidth: 150 }}>
                <InputLabel id="select-site-label">Select a Site</InputLabel>
                <Select labelId="select-site-label" id="select-site" value={site?.id || ""} label="Select a Site" onChange={handleChangeSite}>
                  <MenuItem key={"all"} value={"all"}>
                    All
                  </MenuItem>
                  {sortByKey(sites?.data, "site_name")?.map((s: any) => (
                    <MenuItem key={s?.site_id} value={s?.site_id}>
                      {s?.site_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}
          {isUnitsLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                marginTop: "40px",
                marginBottom: "10px",
                minWidth: 150,
              }}
            >
              <CircularProgress size={24} />
            </Box>
          ) : (
            <>
              <FormControl variant="standard" sx={{ minWidth: 150 }}>
                <InputLabel id="demo-simple-select-label">Select a Unit</InputLabel>
                <Select labelId="demo-simple-select-label" id="demo-simple-select" value={unit?.id ?? ""} label="Select a Unit" onChange={handleChangeUnit}>
                  <MenuItem key={"all"} value={"all"}>
                    All
                  </MenuItem>
                  {sortByKey(units?.data, "unit_name")?.map((u: any) => (
                    <MenuItem key={u.unit_id} value={u.unit_id}>
                      {u.unit_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}
